// Модуль «Рейсы» — логика экрана без Vue (проверяется тестами tests/unit/tripsBoard.test.mjs). [Claude, 04.11.2026]
// Данные и правила считает сервер (erp-backend src/trips_board); здесь — адрес страницы, тексты и форматы.
import { formatPlaceTime } from '../../pages/trip/helpers/placeTime.mjs'

export const NOUN = { one: 'рейс', few: 'рейса', many: 'рейсов' }

// Состояние экрана живёт в адресе: ссылкой можно поделиться, «Назад» из рейса возвращает тот же список.
export const DEFAULTS = Object.freeze({
  scope: 'active', view: 'all', q: '', stage: '', light: '', vehicle: '', responsible: '', top: '',
  load_from: '', load_to: '', sort: 'urgency',
})
const KEYS = Object.keys(DEFAULTS)

export function stateFromQuery(query = {}) {
  const state = { ...DEFAULTS }
  for (const key of KEYS) {
    const value = query[key]
    if (typeof value === 'string' && value.trim()) state[key] = value.trim()
  }
  if (!['active', 'all'].includes(state.scope)) state.scope = 'active'
  if (state.scope === 'all' && state.sort === 'urgency') state.sort = 'load_desc'
  return state
}

export function queryFromState(state) {
  const query = {}
  for (const key of KEYS) {
    const value = state[key]
    if (value !== undefined && value !== null && String(value) !== '' && String(value) !== DEFAULTS[key]) {
      query[key] = String(value)
    }
  }
  return query
}

// Параметры запроса к серверу: этап и светофор — списком, остальное как есть.
export function apiParams(state, { offset = 0, limit } = {}) {
  const params = {}
  for (const key of KEYS) {
    const value = state[key]
    if (value === '' || value === undefined || value === null) continue
    if (key === 'stage') params.stages = value
    else if (key === 'light') params.lights = value
    else params[key] = value
  }
  if (offset) params.offset = offset
  if (limit) params.limit = limit
  return params
}

export function isFiltered(state) {
  return KEYS.some((key) => key !== 'scope' && key !== 'sort' && state[key] !== DEFAULTS[key])
}

export function withChange(state, patch) {
  const next = { ...state, ...patch }
  if (patch.scope === 'all') {
    next.view = 'all'
    next.light = ''
    if (next.sort === 'urgency') next.sort = 'load_desc'
  }
  if (patch.scope === 'active' && next.sort === 'load_desc') next.sort = 'urgency'
  return next
}

// ---------------------------------------------------------------- тексты

export function plural(n, one, few, many) {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b === 1) return one
  if (b >= 2 && b <= 4) return few
  return many
}

export function durationText(minutes) {
  const m = Math.abs(Math.round(minutes))
  const d = Math.floor(m / 1440)
  const h = Math.floor((m % 1440) / 60)
  const mi = m % 60
  if (d >= 2) return `${d} ${plural(d, 'день', 'дня', 'дней')}${h ? ` ${h} ч` : ''}`
  if (d === 1) return `1 день${h ? ` ${h} ч` : ''}`
  if (h) return `${h} ч${mi ? ` ${mi} мин` : ''}`
  return `${mi} мин`
}

// «Срок данных через 3 ч» / «погрузка была 2 ч назад» — что ближе всего по этапу рейса.
export function dueText(row) {
  const m = row?.minutes_left
  if (row?.closed || m === null || m === undefined) return ''  // у завершённого рейса сроков нет
  const what = row.stage === 'need_executor' ? 'срок данных' : row.stage === 'in_transit' ? 'выгрузка' : 'погрузка'
  if (m >= 0) return `${what} через ${durationText(m)}`
  return row.stage === 'need_executor' ? `срок данных прошёл ${durationText(m)} назад` : `${what} была ${durationText(m)} назад`
}

export function inworkText(minutes) {
  if (minutes === null || minutes === undefined) return ''
  if (minutes <= 0) return 'время «В работе» вышло — рейс возвращается в очередь'
  return `в работе: осталось ${durationText(minutes)}`
}

export function placeTime(value, tz) {
  return value ? formatPlaceTime(value, tz) : '—'
}

export function money(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return `${Math.round(Number(value)).toLocaleString('ru-RU')} ₽`
}

export function moneyLine(row) {
  if (!row?.money) return ''
  const parts = []
  if (row.money.customer) parts.push(`ставка ${money(row.money.customer)}`)
  if (row.money.carrier) parts.push(`перевозчику ${money(row.money.carrier)}`)
  if (parts.length) parts.push(row.money.nds ? 'с НДС' : 'без НДС')
  return parts.join(' · ')
}

export const LIGHT_TITLES = { red: 'Нарушение', yellow: 'Под риском', green: 'В норме', grey: 'Нет данных', done: 'Завершён' }

export function shownText(count, total) {
  if (!total) return ''
  if (count >= total) return `Показаны все ${total} ${plural(total, NOUN.one, NOUN.few, NOUN.many)}`
  return `Показано ${count} из ${total}`
}

export function emptyText(state) {
  if (state.q) return `По запросу «${state.q}» рейсов нет. Проверьте номер или напишите город, получателя, фамилию.`
  if (isFiltered(state)) return 'По этим отборам рейсов нет. Снимите часть отборов или нажмите «Сбросить».'
  if (state.scope === 'active') return 'Активных рейсов в окне нет — всё спокойно. Старые рейсы — во вкладке «Все рейсы».'
  return 'Рейсов нет.'
}

// Чипы активных отборов — чтобы было видно, что применено, и снять одним нажатием.
export function activeChips(state, meta = {}) {
  const chips = []
  const title = (list, key) => (list || []).find((i) => i.key === key || i.id === key)?.title
    || (list || []).find((i) => i.key === key || i.id === key)?.name || key
  if (state.stage) chips.push({ key: 'stage', text: `Этап: ${title(meta.stages, state.stage)}` })
  if (state.light) chips.push({ key: 'light', text: LIGHT_TITLES[state.light] || state.light })
  if (state.vehicle) chips.push({ key: 'vehicle', text: `Тип ТС: ${title(meta.vehicles, state.vehicle)}` })
  if (state.responsible) {
    chips.push({ key: 'responsible', text: state.responsible === 'me' ? 'Ответственный: я' : `Ответственный: ${title(meta.managers, state.responsible)}` })
  }
  if (state.top === '1') chips.push({ key: 'top', text: 'Топ-8' })
  if (state.load_from || state.load_to) {
    chips.push({ key: 'dates', text: `Погрузка ${state.load_from || '…'} — ${state.load_to || '…'}` })
  }
  return chips
}

export function clearChip(state, key) {
  if (key === 'dates') return { ...state, load_from: '', load_to: '' }
  return { ...state, [key]: '' }
}
